import {
  LazyQueryExecFunction,
  OperationVariables,
  useMutation
} from '@apollo/client'
import {
  Button,
  Checkbox,
  FormControlLabel,
  ListItem,
  Menu,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import CreateOutlinedIcon from '@material-ui/icons/CreateOutlined'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import LocalAtmOutlinedIcon from '@material-ui/icons/LocalAtmOutlined'
import MeetingRoomIcon from '@material-ui/icons/MeetingRoom'
import PolicyIcon from '@material-ui/icons/Policy'
import parse from 'html-react-parser'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import AdoPayLogo from '../../../../assets/images/adoPayLogo.svg'
import { cartDetailsVar } from '../../../../cache/cart'
import { ChangeModal } from '../../../../components/ChangeModal'
import CustomCounter from '../../../../components/CustomCounter'
import CustomTooltip from '../../../../components/CustomTooltip.tsx'
import {
  ADD_ITEM_TO_CART_MUTATION,
  CREATE_CART_MUTATION
} from '../../../../graphql/cartCrud'
import { addCommaToPrice } from '../../../../helpers/numbers'
import { errorNoCartFound, extractError } from '../../../../helpers/regex'
import { stripHtmlTags } from '../../../../helpers/stripHTML'
import { cleanCartData } from '../../../../helpers/userData'
import { getTranslate, lang } from '../../../../localization'
import {
  AddItemToCartOutput,
  CartResponse,
  CreateCart,
  ItemInput,
  ItemsInCart
} from '../../../../types/cart'
import { Currency } from '../../../../types/currency'
import {
  Occupancy,
  RatePlanForSearch,
  SearchAvailabilityInput
} from '../../../../types/searchAvailability'
import RoomAvailable from '../RoomAvailable'
import { CompareAtPrice, DiscountBadge } from './CompareAtPrice'
import { DisplayRateBreakouts } from './DisplayRateBreakouts'
import {
  ToolTipContentForCancelPolicy,
  ToolTipContentForModifyPolicy
} from './PolicyText'
import { TotalPriceTooltip } from './PriceText'
import {
  availabilityNotice,
  formatBalance,
  getBookabilityLabel
} from './helpers'
import useStyles from './styles/index.styles'

export type Props = {
  ratePlan: RatePlanForSearch
  paymentTermsBalance: {
    walletName: string
    walletID: string
    creditorID?: string
    // Optional on purpose: a wallet can be known to fund this rate plan while WHICH of its
    // creditors does so is not knowable client-side. Rendered as unknown, never as zero.
    cashBalance?: number
    remainigCreditBalance?: number
  }[]
  currency: Currency
  disable: boolean
  changeProvider: boolean
  validProviderCode: string
  setValidProviderCode: React.Dispatch<React.SetStateAction<string>>
  roomTypeCode: string
  searchAvailabilityInput: SearchAvailabilityInput
  occupancy: Occupancy
  selectedWalletType: {
    walletName: string
    walletID: string
  }
  getCart: LazyQueryExecFunction<
    {
      cartResponse: CartResponse
    },
    OperationVariables
  >
  loadingForGetCart: boolean
  setSelectedItemHash: React.Dispatch<React.SetStateAction<string>>
  selectedItemHash: string
  cartData: CartResponse | undefined
  uniqueProviders: {
    title: string
    value: string
    checked: boolean
  }[]
  itemsInCart: ItemsInCart
  setItemsInCart: React.Dispatch<React.SetStateAction<ItemsInCart>>
}

const RatePlanRow = ({
  ratePlan,
  paymentTermsBalance,
  currency,
  disable,
  changeProvider,
  validProviderCode,
  setValidProviderCode,
  roomTypeCode,
  searchAvailabilityInput,
  occupancy,
  selectedWalletType,
  getCart,
  loadingForGetCart,
  selectedItemHash,
  setSelectedItemHash,
  uniqueProviders,
  itemsInCart,
  setItemsInCart
}: Props) => {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const params = useParams<{ channelId: string; code: string; name: string }>()

  const [useAllocated, setuseAllocated] = useState<boolean>(false)

  // Derived beside the other rate-row values rather than computed inline in
  // JSX, matching how cancelPolicyContent / modifyPolicyContent are handled.
  const counterNotice = availabilityNotice({
    bookabilityStatus: ratePlan.bookabilityStatus,
    useAllocated,
    pooledAvailability: ratePlan.pooledAvailability,
    allocatedAvailability: ratePlan.allocatedAvailability
  })

  // Interpolated, never concatenated: the translator has to be able to place
  // the number and the control name inside the sentence. Persian puts a counted
  // noun mid-sentence, and a trailing "(10)" in this row is ambiguous against
  // the price, the night count and the occupancy figure sitting beside it.
  const counterNoticeText =
    counterNotice.kind === 'use-allocated'
      ? getTranslate(
          'No unallocated rooms left for these dates. Tick "{label}" to book from the {count} rooms allocated to this channel',
          {
            label: getTranslate('Use of Allocated'),
            count: counterNotice.allocated
          }
        )
      : counterNotice.kind === 'use-pool'
      ? getTranslate(
          'No allocated rooms left for these dates. Untick "{label}" to book from the {count} rooms in the shared pool',
          {
            label: getTranslate('Use of Allocated'),
            count: counterNotice.pooled
          }
        )
      : counterNotice.kind === 'sold-out'
      ? getTranslate('No rooms available for these dates')
      : ''
  const [showModal, setShowModal] = useState(false)
  const [pendingProvider, setPendingProvider] = useState<string | null>(null)

  const [anchorEl, setAnchorEl] = useState<
    (EventTarget & HTMLButtonElement) | null
  >(null)

  const handleClose = () => {
    setAnchorEl(null)
  }
  const cancelPolicyContent = ToolTipContentForCancelPolicy(ratePlan, classes)
  const modifyPolicyContent = ToolTipContentForModifyPolicy(ratePlan, classes)
  //CREATE_CART_MUTATION
  const [createCart] = useMutation<{ createCart: CreateCart }>(
    CREATE_CART_MUTATION,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (res) => {
        const newCartDetails = {
          channelID: params.channelId,
          channelCode: params.code,
          channelName: params.name,
          propertyCode: searchAvailabilityInput?.propertyCode || '',
          currrncy: searchAvailabilityInput.currency
        }
        setItemsInCart({ ...itemsInCart, cartID: res.createCart.cartID })
        cartDetailsVar(newCartDetails)
        localStorage.setItem('cartDetails', JSON.stringify(newCartDetails))
        localStorage.setItem('cartID', res.createCart.cartID)
        getCart({
          variables: {
            cartID: res.createCart.cartID
          }
        })
      },
      onError: (err) => {
        toast.error(err.message)
      }
    }
  )

  //ADD_ITEM_TO_CART_MUTATION
  const [addItem, { loading: loadingForAddItem }] = useMutation<{
    addItemToCart: AddItemToCartOutput
  }>(ADD_ITEM_TO_CART_MUTATION, {
    notifyOnNetworkStatusChange: true,
    onCompleted: () => {
      if (localStorage.getItem('cartID')) {
        getCart({
          variables: {
            cartID: localStorage.getItem('cartID')
          }
        })
      }
    },
    onError: (err) => {
      // graphQLErrors is empty on a network failure and absent when the value reaching here
      // is not an ApolloError, so indexing [0] crashes the error handler itself.
      const firstGraphQLError = err?.graphQLErrors?.[0]
      if (
        firstGraphQLError &&
        extractError(firstGraphQLError.extensions) === errorNoCartFound
      ) {
        toast.error(err.message)
        cleanCartData()
        localStorage.removeItem('confirmPrimitiveInput')
        localStorage.removeItem('confirmPrimitiveOutput')
        localStorage.removeItem('hotelDetails')
        localStorage.removeItem('primitiveData')
        createCart({
          variables: {
            input: {
              channelCode: params.code,
              currency: searchAvailabilityInput.currency,
              propertyCode: searchAvailabilityInput.propertyCode
            }
          }
        })
      } else {
        toast.error(err.message)
        if (localStorage.getItem('cartID')) {
          getCart({
            variables: {
              cartID: localStorage.getItem('cartID')
            }
          })
        }
      }
    }
  })

  const toolTipForBalanceDetails = (input: {
    walletName: string
    cashBalance?: number
    remainigCreditBalance?: number
  }): JSX.Element => {
    return (
      <div className={classes.main} dir={lang === 'fa' ? 'rtl' : 'ltr'}>
        <div className={classes.header}>
          <Typography variant="h5" className={classes.toolTipText}>
            {getTranslate('Balance Details')}
          </Typography>
        </div>
        <div className={classes.content}>
          <div className={classes.row}>
            <Typography variant="h6">{getTranslate('Cash Balance')}</Typography>
            <Typography variant="h6">
              {formatBalance(input.cashBalance, currency)}
            </Typography>
          </div>
          <div className={classes.row}>
            <Typography variant="h6">
              {getTranslate('Remaining Credit Balance')}
            </Typography>
            <Typography variant="h6">
              {formatBalance(input.remainigCreditBalance, currency)}
            </Typography>
          </div>
        </div>
      </div>
    )
  }

  const handleMoreIconClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    setAnchorEl(event.currentTarget)
  }

  return (
    <div
      className={[classes.ratePlan, disable && classes.ratePlanDisabled].join(
        ' '
      )}>
      <div className={classes.left}>
        <div className={classes.ratePlanTitle}>
          <Typography variant="subtitle1" className={classes.iconText}>
            <MeetingRoomIcon className={classes.icon} />
            {`${ratePlan.ratePlanName} (${ratePlan.displayRatePlanCode})`}
          </Typography>
          {!isMobile &&
          ((ratePlan.pooledAvailability < 10 &&
            ratePlan.pooledAvailability > 0) ||
            (useAllocated &&
              ratePlan.allocatedAvailability > 0 &&
              ratePlan.allocatedAvailability < 10)) ? (
            <RoomAvailable
              availableRoom={
                useAllocated
                  ? ratePlan.allocatedAvailability
                  : ratePlan.pooledAvailability
              }
            />
          ) : (
            <></>
          )}
        </div>
        <div className={classes.availibilityContainer}>
          <Typography variant="h6" className={classes.greyFontWeight}>
            {`${getTranslate('Board Type')}: ${getTranslate(
              ratePlan.boardType
            )}`}
          </Typography>
          {isMobile &&
            ((ratePlan.pooledAvailability < 10 &&
              ratePlan.pooledAvailability > 0) ||
              (useAllocated &&
                ratePlan.allocatedAvailability > 0 &&
                ratePlan.allocatedAvailability < 10)) && (
              <RoomAvailable availableRoom={ratePlan.pooledAvailability} />
            )}
        </div>
        <div className={classes.policy}>
          {cancelPolicyContent ? (
            <CustomTooltip
              title={cancelPolicyContent}
              placement="top"
              arrow
              disableHoverListener={disable}
              classes={{ tooltip: classes.toolTip, arrow: classes.arrow }}>
              <div className={classes.policy}>
                <PolicyIcon className={classes.redIcon} />
                <Typography variant="h6" className={classes.redtext}>
                  {getTranslate('Cancellation Policy')}
                </Typography>
                <InfoOutlinedIcon className={classes.infoIcon} />
              </div>
            </CustomTooltip>
          ) : (
            <div className={classes.policy}>
              <CheckCircleIcon className={classes.greenIcon} />
              <Typography
                variant={isMobile ? 'h6' : 'h5'}
                className={classes.greenIconText}>
                {getTranslate('Free Cancellation')}
              </Typography>
            </div>
          )}
        </div>
        <div className={classes.policy}>
          {modifyPolicyContent ? (
            <CustomTooltip
              title={modifyPolicyContent}
              placement="top"
              arrow
              disableHoverListener={disable}
              classes={{ tooltip: classes.toolTip, arrow: classes.arrow }}>
              <div className={classes.tooltipRow}>
                <CreateOutlinedIcon className={classes.yellowIcon} />
                <Typography variant="h5" className={classes.yellowText}>
                  {getTranslate('Modify Policy')}
                </Typography>
                <InfoOutlinedIcon className={classes.infoIcon} />
              </div>
            </CustomTooltip>
          ) : (
            <div className={classes.policy}>
              <CheckCircleIcon className={classes.greenIcon} />
              <Typography
                variant={isMobile ? 'h6' : 'h5'}
                className={classes.greenIconText}>
                {getTranslate('Free Modification')}
              </Typography>
            </div>
          )}
        </div>
        <div className={classes.policy}>
          <>
            {ratePlan.ratePolicy ? (
              <CustomTooltip
                title={
                  <div className={classes.main}>
                    <div className={classes.header}>
                      <Typography variant="h6" className={classes.toolTipText}>
                        {getTranslate('Rate Policy')}
                      </Typography>
                    </div>
                    <div
                      className={classes.content}
                      style={{
                        direction: lang === 'fa' ? 'rtl' : 'ltr', // make layout direction dynamic
                        textAlign: lang === 'fa' ? 'right' : 'left', // align text accordingly
                        whiteSpace: 'pre-line', // keep newlines from stripHtmlTags
                        unicodeBidi: 'embed' // handle mixed RTL/LTR properly
                      }}>
                      {parse(
                        stripHtmlTags(ratePlan.ratePolicy).replace(
                          /\n/g,
                          '<br/>'
                        )
                      )}
                    </div>
                  </div>
                }
                placement="top"
                arrow
                disableHoverListener={disable}
                classes={{ tooltip: classes.toolTip, arrow: classes.arrow }}>
                <div className={classes.policy}>
                  <LocalAtmOutlinedIcon className={classes.yellowIcon} />
                  <Typography variant="h6" className={classes.yellowText}>
                    {getTranslate('Rate Policy')}
                  </Typography>
                  <InfoOutlinedIcon className={classes.infoIcon} />
                </div>
              </CustomTooltip>
            ) : (
              <></>
            )}
          </>
        </div>
      </div>
      <div className={classes.middle}>
        <div className={classes.provider}>
          <Typography variant="h5" className={classes.greyFontWeight}>
            {`${getTranslate('Provider')}: `}
          </Typography>
          <Typography variant="h6" className={classes.blackText}>
            {ratePlan.providerName}
          </Typography>
        </div>
        <Typography variant="h5" className={classes.greyFontWeight}>
          {`${getTranslate('Payment Term')}: `}
        </Typography>
        <div className={classes.providerRow}>
          {ratePlan.availablePaymentMethods.length > 0 ? (
            ratePlan.availablePaymentMethods.map((method, idx) => (
              <Tooltip
                key={idx}
                title={
                  paymentTermsBalance?.find((p) => p.walletName === method)
                    ? toolTipForBalanceDetails(
                        paymentTermsBalance.find(
                          (p) => p.walletName === method
                        )!
                      )
                    : toolTipForBalanceDetails({
                        cashBalance: undefined,
                        remainigCreditBalance: undefined,
                        walletName: method
                      })
                }
                placement="top"
                arrow
                disableHoverListener={disable}
                classes={{
                  tooltip: classes.toolTip,
                  arrow: classes.arrow
                }}>
                <div key={method} className={classes.payMentTermRow}>
                  <AccountBalanceWalletIcon className={classes.walletIcon} />
                  {method === 'AdoPay' ? (
                    <img src={AdoPayLogo} className={classes.adopayLogo} />
                  ) : (
                    <Typography
                      variant="h6"
                      className={classes.peymentTermName}>
                      {getTranslate(method)}
                    </Typography>
                  )}
                  <InfoOutlinedIcon className={classes.infoIcon} />
                </div>
              </Tooltip>
            ))
          ) : (
            <Typography variant="h6" className={classes.redtext}>
              {getTranslate('No payment term Available')}
            </Typography>
          )}
        </div>
      </div>
      <div className={classes.right}>
        <div>
          <CompareAtPrice
            price={ratePlan.price}
            className={classes.discountText}
          />
          <div className={classes.totalAmount}>
            <div className={classes.tootalDiscount}>
              <Typography variant="h5" className={classes.greyFontWeight}>
                {getTranslate('Total Amount')}
              </Typography>
              <DiscountBadge
                price={ratePlan.price}
                wrapperClassName={classes.discount}
                textClassName={classes.greenText}
              />
            </div>
            <Tooltip
              title={<TotalPriceTooltip ratePlan={ratePlan} />}
              placement="top"
              arrow
              disableHoverListener={disable}
              classes={{ tooltip: classes.toolTip, arrow: classes.arrow }}>
              <div className={classes.price}>
                <InfoOutlinedIcon className={classes.priceInfoIcon} />
                <Typography variant="h4" className={classes.blackText}>
                  {`${addCommaToPrice(
                    ratePlan.price.afterDiscount.totalPrice
                  )} ${getTranslate(ratePlan.price.currency || '')}`}
                </Typography>
              </div>
            </Tooltip>
            <DisplayRateBreakouts
              rateBreakouts={ratePlan.price.rateBreakouts}
              className={classes.greyFontWeight}
            />
          </div>
        </div>
        <div className={classes.counterBlock}>
          <div className={classes.counter}>
            {ratePlan.allocatedAvailability !== 0 ? (
              <FormControlLabel
                className={classes.formcontrol}
                label={getTranslate('Use of Allocated')}
                classes={{ root: classes.checkBoxRoot }}
                control={
                  <Checkbox
                    onChange={(event) => setuseAllocated(event.target.checked)}
                    color="primary"
                    size="small"
                    disabled={disable || ratePlan.allocatedAvailability == 0}
                  />
                }
              />
            ) : (
              <div />
            )}

            {ratePlan.bookabilityStatus !== 'BOOKABLE' ? (
              <Button variant="text" disabled>
                <Typography variant="h5" className={classes.greyText}>
                  {getTranslate(
                    getBookabilityLabel(ratePlan.bookabilityStatus, occupancy)
                  )}
                </Typography>
              </Button>
            ) : changeProvider ? (
              <>
                <Button variant="text" onClick={handleMoreIconClick}>
                  <Typography variant="h5" className={classes.greenText}>
                    {getTranslate('Change Provider')}
                  </Typography>
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  keepMounted
                  open={Boolean(anchorEl)}
                  onClose={handleClose}>
                  {uniqueProviders
                    .filter((p) => p.value !== validProviderCode)
                    .map((p) => (
                      <ListItem
                        button
                        onClick={() => {
                          setPendingProvider(p.value)
                          setShowModal(true)
                        }}>
                        {p.title}
                      </ListItem>
                    ))}
                </Menu>
                <ChangeModal
                  title="Your Provider is Being Changed. If you confirmed, other items with different Provider will be deleted"
                  isOpen={showModal}
                  onClick={() => {
                    if (pendingProvider) {
                      setValidProviderCode(pendingProvider)
                    }
                    setAnchorEl(null)
                    setShowModal(false)
                  }}
                  onClose={() => {
                    setAnchorEl(null)
                    setShowModal(false)
                  }}
                />
              </>
            ) : (
              <CustomCounter
                setValue={(val) => {
                  if (
                    localStorage.getItem('cartID') &&
                    paymentTermsBalance.find(
                      (item) => item.walletName == selectedWalletType.walletName
                    )?.walletID
                  ) {
                    setSelectedItemHash(ratePlan.hash)
                    const currentProviderCode = ratePlan.providerCode
                    const newItem: ItemInput = {
                      adults: occupancy.adults,
                      arrival: searchAvailabilityInput.arrival,
                      departure: searchAvailabilityInput.departure,
                      extraBeds: occupancy.extraBeds,
                      providerCode: currentProviderCode,
                      ratePlanCode: ratePlan.ratePlanCode,
                      roomTypeCode: roomTypeCode,
                      quantity: val,
                      useAllocated: useAllocated,
                      price: ratePlan.price.afterDiscount.totalPrice
                    }
                    const { price, ...restItem } = newItem

                    addItem({
                      variables: {
                        input: {
                          cartID: localStorage.getItem('cartID'),
                          walletID:
                            paymentTermsBalance.find(
                              (item) =>
                                item.walletName == selectedWalletType.walletName
                            )?.walletID || 0,
                          creditorID:
                            paymentTermsBalance.find(
                              (item) =>
                                item.walletName == selectedWalletType.walletName
                            )?.creditorID || undefined,
                          itemsInput: [restItem]
                        }
                      }
                    })
                  }
                }}
                value={
                  useAllocated
                    ? ratePlan.count! <= ratePlan.allocatedAvailability
                      ? ratePlan.count!
                      : 0
                    : ratePlan.count! <= ratePlan.pooledAvailability
                    ? ratePlan.count!
                    : 0
                }
                max={
                  useAllocated
                    ? ratePlan.allocatedAvailability
                    : ratePlan.pooledAvailability
                }
                // Within this branch the plan is BOOKABLE, so a notice exists
                // exactly when the effective availability is 0 — i.e. when the
                // increase step is a no-op.
                increaseBlocked={counterNotice.kind !== 'none'}
                increaseDescribedBy={`availability-notice-${ratePlan.hash}`}
                increaseLabel={getTranslate('Add one room')}
                decreaseLabel={getTranslate('Remove one room')}
                loading={
                  selectedItemHash === ratePlan.hash &&
                  (loadingForAddItem || loadingForGetCart)
                }
                disable={
                  loadingForAddItem ||
                  loadingForGetCart ||
                  selectedWalletType.walletName === '' ||
                  disable ||
                  !ratePlan.availablePaymentMethods.includes(
                    selectedWalletType.walletName
                  ) ||
                  !itemsInCart.cartID
                }
              />
            )}
          </div>

          {/* Pre-mounted rather than conditionally rendered: a live region
              inserted together with its text is announced unreliably, so the
              region exists from first paint and only its content changes. */}
          <Typography
            variant="body2"
            component="p"
            id={`availability-notice-${ratePlan.hash}`}
            role="status"
            aria-live="polite"
            aria-atomic="true"
            data-cy={
              counterNotice.kind === 'none'
                ? 'availability-notice-empty'
                : `availability-${counterNotice.kind}`
            }
            className={classes.availabilityHint}>
            {counterNoticeText}
          </Typography>
        </div>
      </div>
    </div>
  )
}

export default RatePlanRow
